import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import TcnAdminLayout from '../../components/tcn-admin/TcnAdminLayout';
import CreatorNetworkIdsEditor from '../../components/tcn-admin/CreatorNetworkIdsEditor';

/**
 * /tcn/admin/settings — Agency owner self-service settings.
 *
 * Phase 1 scope: Creator Network ID per server. Lets the owner finalize the
 * agency profile after super_admin creates the row. The TikTok faction_id is
 * needed before any Phase 2 API/scraping integration can address this agency.
 */
const TcnAdminSettings = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { agencyId, tenantSlug } = useTenant();
  const [agency, setAgency] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    if (!agencyId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const { data, error: dbErr } = await supabase
      .from('agencies')
      .select('id, name, slug, servers, creator_network_ids, active, tenant_id')
      .eq('id', agencyId)
      .maybeSingle();
    if (dbErr) setError(dbErr.message);
    setAgency(data || null);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agencyId]);

  if (!user) return null;

  return (
    <TcnAdminLayout
      title={t('tcn.admin.settings.title', 'Agency settings')}
      subtitle={t('tcn.admin.settings.subtitle', 'Configure your agency details — visible to super_admin and used by Phase 2 integrations.')}
    >
      {loading && (
        <div className="text-sm text-muted-foreground">{t('common.loading', 'Loading…')}</div>
      )}
      {error && (
        <div className="rounded-xl bg-error/10 border border-error/30 px-4 py-3 text-sm text-error">{error}</div>
      )}
      {!loading && !agency && (
        <div className="rounded-xl bg-muted/40 border border-border px-4 py-3 text-sm text-muted-foreground">
          {t('tcn.admin.settings.noAgency', 'No agency linked to your account. Contact your super_admin.')}
        </div>
      )}

      {agency && (
        <div className="space-y-6 max-w-2xl">
          <section className="rounded-2xl bg-card border border-border p-5">
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-3">
              {t('tcn.admin.settings.identity', 'Identity')}
            </div>
            <div className="grid grid-cols-2 gap-y-2 text-sm">
              <span className="text-muted-foreground">{t('tcn.admin.settings.agencyName', 'Agency name')}</span>
              <span className="text-foreground font-medium">{agency.name}</span>
              <span className="text-muted-foreground">{t('tcn.admin.settings.tenant', 'Tenant')}</span>
              <span className="text-foreground font-data uppercase">{tenantSlug ?? '—'}</span>
              <span className="text-muted-foreground">{t('tcn.admin.settings.servers', 'Servers')}</span>
              <span className="text-foreground font-data">{(agency.servers ?? []).join(', ') || '—'}</span>
            </div>
          </section>

          <section className="rounded-2xl bg-card border border-border p-5">
            <div className="mb-4">
              <div className="text-base font-semibold text-foreground">
                {t('tcn.admin.cnid.heading', 'Creator Network ID')}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {t(
                  'tcn.admin.settings.cnidHelp',
                  'Your TikTok Backstage faction_id, also displayed as "Creator Network ID". One value per server you operate on. Required before any Phase 2 API/scraping integration.',
                )}
              </p>
            </div>
            <CreatorNetworkIdsEditor agency={agency} onSaved={() => load()} compact />
          </section>
        </div>
      )}
    </TcnAdminLayout>
  );
};

export default TcnAdminSettings;
